
import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { SubscriptionService, Service } from '@/lib/data';
import { cn } from '@/lib/utils';
import ServiceSelectionList from './ServiceSelectionList';
import { SelectedServiceState, ServiceFormValues } from '@/types/serviceTypes';

const serviceSchema = z.object({
  service_id: z.string(),
  status: z.enum(['registered', 'active', 'cancelled', 'terminated']),
  registration_date: z.date(),
  activation_date: z.date().nullable(),
  termination_date: z.date().nullable(),
  cancellation_date: z.date().nullable(),
  price: z.coerce.number().nullable(),
});

interface ServiceManagementDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  editingService: SubscriptionService | null;
  isAddingService: boolean;
  subscription: { subscription_id: string };
  selectedServices: Record<string, SelectedServiceState>;
  setSelectedServices: React.Dispatch<React.SetStateAction<Record<string, SelectedServiceState>>>;
  availableServices: Service[];
  isLoading: boolean;
  onSubmit: (data: ServiceFormValues) => Promise<void>;
  groupServicesByParent: (services: Service[]) => { parent: Service, children: Service[] }[];
  getAvailableServices: () => Service[];
  toggleServiceSelection: (serviceId: string) => void;
  updateServicePrice: (serviceId: string, price: number | null) => void;
}

const ServiceManagementDialog: React.FC<ServiceManagementDialogProps> = ({
  isOpen,
  setIsOpen,
  editingService,
  isAddingService,
  subscription,
  selectedServices,
  availableServices,
  isLoading,
  onSubmit,
  groupServicesByParent,
  getAvailableServices,
  toggleServiceSelection,
  updateServicePrice
}) => {
  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      service_id: '',
      status: 'active',
      registration_date: new Date(),
      activation_date: null,
      termination_date: null,
      cancellation_date: null,
      price: null,
    }
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      if (editingService) {
        form.reset({
          service_id: editingService.service_id,
          status: editingService.status as 'active' | 'cancelled' | 'terminated', 
          registration_date: editingService.registration_date ? new Date(editingService.registration_date) : new Date(),
          activation_date: editingService.activation_date ? new Date(editingService.activation_date) : null,
          termination_date: editingService.termination_date ? new Date(editingService.termination_date) : null,
          cancellation_date: editingService.cancellation_date ? new Date(editingService.cancellation_date) : null,
          price: editingService.price,
        });
      } else {
        form.reset({
          service_id: '',
          status: 'active',
          registration_date: new Date(),
          activation_date: null,
          termination_date: null,
          cancellation_date: null,
          price: null,
        });
      }
    }
  }, [editingService, form, isOpen]);

  const handleFormSubmit = form.handleSubmit(async (data) => {
    console.log("Form submitted with data:", data);
    console.log("Current selected services state:", 
      Object.entries(selectedServices)
        .filter(([_, state]) => state && state.selected)
        .map(([id]) => id)
    );
    
    try {
      await onSubmit(data);
      setIsOpen(false); // Close dialog after successful submit
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  // Check if at least one service is selected when adding services
  const hasSelectedServices = React.useMemo(() => {
    if (!isAddingService) return true;
    return Object.values(selectedServices).some(s => s && s.selected);
  }, [isAddingService, selectedServices]);

  // Enhanced grouping logic that shows child services under their actual parent
  const getServiceGroups = () => {
    const availableServicesList = getAvailableServices();
    
    console.log("=== ServiceManagementDialog DEBUG ===");
    console.log("Available services for grouping:", availableServicesList.map(s => ({ 
      id: s.service_id, 
      name: s.service_name, 
      parent: s.parent_service_id 
    })));
    
    // Get all parent services that are available (not in subscription)
    const availableParents = availableServicesList.filter(service => !service.parent_service_id);
    
    // Get all child services that are available (not in subscription)
    const availableChildren = availableServicesList.filter(service => service.parent_service_id);
    
    // Create groups for available parent services
    const groups = availableParents.map(parent => ({
      parent,
      children: availableChildren.filter(child => child.parent_service_id === parent.service_id),
      isParentSelectable: true
    }));
    
    // Handle orphaned child services (whose parent is already in subscription)
    const orphanedChildren = availableChildren.filter(child => 
      !availableParents.some(parent => parent.service_id === child.parent_service_id)
    );
    
    // Group orphaned children by their actual parent service
    const orphanedGroups: { [key: string]: Service[] } = {};
    orphanedChildren.forEach(child => {
      const parentId = child.parent_service_id!;
      if (!orphanedGroups[parentId]) {
        orphanedGroups[parentId] = [];
      }
      orphanedGroups[parentId].push(child);
    });
    
    // Add orphaned groups using the actual parent service information
    Object.entries(orphanedGroups).forEach(([parentId, children]) => {
      // Find the actual parent service from ALL services (not just available ones)
      const parentService = availableServices.find(s => s.service_id === parentId);
      
      if (parentService) {
        groups.push({
          parent: parentService,
          children: children,
          isParentSelectable: false // Parent is already in subscription, so not selectable
        });
      }
    });
    
    console.log("Final service groups:", groups.map(g => ({
      parent: { id: g.parent.service_id, name: g.parent.service_name },
      children: g.children.map(c => ({ id: c.service_id, name: c.service_name })),
      isParentSelectable: g.isParentSelectable
    })));
    console.log("=== END ServiceManagementDialog DEBUG ===");
    
    return groups;
  };

  // Only render the dialog content when it's open to prevent unnecessary renders
  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {editingService ? 'Update the service details below.' : 'Select services to add to this subscription.'}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(async (data) => {
            console.log("Form submitted with data:", data);
            console.log("Current selected services state:", 
              Object.entries(selectedServices)
                .filter(([_, state]) => state && state.selected)
                .map(([id]) => id)
            );
            
            try {
              await onSubmit(data);
              setIsOpen(false);
            } catch (error) {
              console.error("Form submission error:", error);
            }
          })} className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {isAddingService ? (
              <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
                <h3 className="text-sm font-medium">Available Services</h3>
                
                {getAvailableServices().length === 0 ? (
                  <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
                    All available services have already been added to this subscription.
                  </div>
                ) : (
                  <ServiceSelectionList 
                    groups={getServiceGroups()}
                    selectedServices={selectedServices}
                    toggleServiceSelection={toggleServiceSelection}
                    updateServicePrice={updateServicePrice}
                  />
                )}
              </div>
            ) : (
              <FormField
                control={form.control}
                name="service_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service</FormLabel>
                    <FormControl>
                      <Input 
                        type="text" 
                        value={editingService?.service.service_name || ''} 
                        disabled={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select 
                    disabled={isLoading}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="registered">Registered</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="registration_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Registration Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                            type="button"
                          >
                            {field.value ? (
                              format(field.value, "PP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-auto p-0 pointer-events-auto" 
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={isLoading}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="activation_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Activation Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                            type="button"
                          >
                            {field.value ? (
                              format(field.value, "PP")
                            ) : (
                              <span>Optional</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-auto p-0 pointer-events-auto" 
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          disabled={isLoading}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {!isAddingService && (
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter price"
                        disabled={isLoading}
                        {...field}
                        value={field.value === null ? '' : field.value}
                        onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            <div className="flex justify-end gap-2 pt-4 mt-auto">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
                size="sm"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isLoading || (isAddingService && (!hasSelectedServices || getAvailableServices().length === 0))}
                size="sm"
              >
                {isLoading ? "Saving..." : (
                  editingService ? "Update" : "Add"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceManagementDialog;
